import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export function SiteContainer({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('mx-auto w-full max-w-6xl px-4', className)}>{children}</div>;
}

export function SiteMockSlot({
  children,
  className,
  tone = 'light',
}: {
  children: ReactNode;
  className?: string;
  tone?: 'light' | 'dark' | 'paper';
}) {
  return (
    <div
      className={cn(
        'flex h-[6.75rem] flex-col justify-center overflow-hidden rounded-md border p-3 text-sm',
        tone === 'dark' && 'border-white/15 bg-white/5',
        tone === 'light' && 'border-[#d9d4cc] bg-white',
        tone === 'paper' && 'border-[#d9d4cc] bg-[#f6f5f3]',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SiteAppFrame({
  module,
  children,
  className,
}: {
  module: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <figure
      className={cn(
        'overflow-hidden rounded-xl border border-[#d9d4cc] bg-[#f6f5f3] shadow-[0_22px_50px_rgba(14,26,40,0.08)]',
        className,
      )}
    >
      <div className="flex min-h-[300px]">
        <div className="hidden w-12 shrink-0 flex-col items-center gap-3 bg-[#0e1a28] py-4 sm:flex" aria-hidden>
          <img src="/logo-simbolo-branco.png" alt="" className="h-6 w-6 object-contain" />
          <span className="mt-1 h-7 w-7 rounded-md bg-[#f5821f]/90" />
          <span className="h-6 w-6 rounded-md bg-white/10" />
          <span className="h-6 w-6 rounded-md bg-white/10" />
          <span className="h-6 w-6 rounded-md bg-white/10" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between border-b border-[#d9d4cc] bg-white px-4 py-2.5">
            <div>
              <p className="text-[11px] text-[#5c5852]">Início / {module}</p>
              <p className="mt-0.5 text-sm font-medium text-[#16181d]">{module}</p>
            </div>
            <p className="text-[11px] text-[#5c5852]">JR Pneus · Centro</p>
          </div>
          <div className="p-4">{children}</div>
        </div>
      </div>
    </figure>
  );
}

export function SiteOsCard({ className }: { className?: string }) {
  return (
    <article
      data-hero-first-frame
      className={cn(
        'overflow-hidden rounded-lg border border-[#d9d4cc] bg-white shadow-[0_8px_24px_rgba(14,26,40,0.06)]',
        className,
      )}
    >
      <div className="border-b border-[#d9d4cc] px-4 py-3">
        <p className="text-[11px] text-[#5c5852]">Início / Ordens de serviço / OS-1042</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <h3 className="font-display text-lg font-semibold text-[#16181d]">OS-1042</h3>
          <span className="rounded-md bg-[#e8f6ee] px-2 py-0.5 text-[11px] font-semibold text-[#1f6b43]">
            Finalizada
          </span>
        </div>
        <p className="mt-1 text-sm text-[#5c5852]">João Pereira · ABC-1234 · Gol 1.6</p>
        <p className="mt-2 inline-block rounded-md bg-[#e8f6ee] px-2.5 py-1 font-os text-sm font-bold text-[#1f6b43]">
          R$ 189,90
        </p>
      </div>
      <div className="px-4 py-3">
        <p className="text-xs font-medium text-[#16181d]">Itens</p>
        <div className="mt-2 overflow-hidden rounded-md border border-[#d9d4cc]">
          <div className="grid grid-cols-[1fr_auto_auto] gap-2 bg-[#f6f5f3] px-3 py-1.5 text-[11px] text-[#5c5852]">
            <span>Descrição</span>
            <span>Qtd</span>
            <span>V. unit.</span>
          </div>
          {[
            ['Serviço', 'Troca de óleo 5W30', '1', 'R$ 129,90'],
            ['Produto', 'Filtro de óleo', '1', 'R$ 60,00'],
          ].map(([tipo, desc, qtd, valor]) => (
            <div key={desc} className="grid grid-cols-[1fr_auto_auto] gap-2 border-t border-[#d9d4cc] px-3 py-2 text-sm">
              <span>
                <span className="mr-2 text-[11px] text-[#5c5852]">{tipo}</span>
                {desc}
              </span>
              <span className="font-os text-[#5c5852]">{qtd}</span>
              <span className="font-os">{valor}</span>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}

export function SiteWhatsAppThread({ className }: { className?: string }) {
  return (
    <figure
      className={cn(
        'overflow-hidden rounded-[1.85rem] border-[8px] border-[#16181d] bg-white shadow-[0_18px_44px_rgba(14,26,40,0.14)]',
        className,
      )}
    >
      <div className="flex items-center gap-3 bg-[#075e54] px-4 py-3 text-white">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-xs font-semibold">
          JR
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-none">JR Pneus e Freios</p>
          <p className="mt-1 text-[11px] text-white/70">online</p>
        </div>
      </div>
      <div className="space-y-2 bg-[#ece5dd] px-3 py-4">
        <div className="max-w-[92%] rounded-lg rounded-tl-sm bg-white px-3 py-2.5 text-[13px] leading-relaxed text-[#16181d] shadow-sm">
          <p>Olá, João! 👋</p>
          <p className="mt-1.5">Faz um tempo que não cuidamos do seu ABC-1234.</p>
          <p className="mt-1.5">Que tal agendar seu retorno? Estamos com horários disponíveis — é só responder por aqui!</p>
          <p className="mt-1.5 text-[#5c5852]">— JR Pneus e Freios</p>
          <p className="mt-2 text-right text-[10px] text-[#5c5852]">09:14</p>
        </div>
        <div className="ml-auto max-w-[80%] rounded-lg rounded-tr-sm bg-[#dcf8c6] px-3 py-2.5 text-[13px] leading-relaxed text-[#16181d] shadow-sm">
          <p>Pode ser quinta, 09:30.</p>
          <p className="mt-2 text-right text-[10px] text-[#5c5852]">09:16</p>
        </div>
      </div>
    </figure>
  );
}

export function SiteOsListMock() {
  const rows = [
    ['OS-1042', 'João Pereira', 'ABC-1234', 'Finalizada', 'R$ 189,90', 'ok'],
    ['OS-1041', 'Marina Costa', 'RTY-8821', 'Em andamento', 'R$ 420,00', 'warn'],
    ['OS-1040', 'Carlos Mello', 'FGH-3301', 'Aberta', 'R$ 90,00', 'open'],
  ] as const;

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-medium text-[#16181d]">Ordens de serviço</p>
        <span className="rounded-md bg-[#0e1a28] px-2.5 py-1 text-[11px] font-medium text-white">Nova OS</span>
      </div>
      <div className="overflow-hidden rounded-md border border-[#d9d4cc] bg-white">
        <div className="grid grid-cols-[auto_1fr_auto_auto] gap-3 bg-[#f6f5f3] px-3 py-2 text-[11px] text-[#5c5852]">
          <span>OS</span>
          <span>Cliente</span>
          <span>Status</span>
          <span>Total</span>
        </div>
        {rows.map(([os, cliente, placa, status, total, tone]) => (
          <div key={os} className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 border-t border-[#d9d4cc] px-3 py-2.5 text-sm">
            <span className="font-os text-[#16181d]">{os}</span>
            <span className="min-w-0">
              <span className="block truncate">{cliente}</span>
              <span className="font-os text-[11px] text-[#5c5852]">{placa}</span>
            </span>
            <span
              className={cn(
                'rounded-md px-2 py-0.5 text-[11px] font-semibold',
                tone === 'ok' && 'bg-[#e8f6ee] text-[#1f6b43]',
                tone === 'warn' && 'bg-[#e8eef8] text-[#2b4c7e]',
                tone === 'open' && 'bg-[#fff4ea] text-[#b15a10]',
              )}
            >
              {status}
            </span>
            <span className="font-os">{total}</span>
          </div>
        ))}
      </div>
    </>
  );
}

export function SitePdvMock() {
  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-medium text-[#16181d]">PDV · caixa aberto</p>
        <span className="font-os text-xs text-[#1f6b43]">R$ 1.240,00</span>
      </div>
      <div className="overflow-hidden rounded-md border border-[#d9d4cc] bg-white">
        {[
          ['Óleo 5W30 1L', '24 → 23', 'R$ 129,90'],
          ['Filtro de óleo', '11 → 10', 'R$ 60,00'],
        ].map(([item, stock, valor], index) => (
          <div key={item} className={cn('flex items-center justify-between px-3 py-2.5 text-sm', index > 0 && 'border-t border-[#d9d4cc]')}>
            <div>
              <p>{item}</p>
              <p className="font-os text-[11px] text-[#f5821f]">{stock}</p>
            </div>
            <span className="font-os">{valor}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className="rounded-md bg-[#0e1a28] px-2.5 py-1 text-[11px] font-medium text-white">PIX</span>
        <span className="font-os text-xl text-[#16181d]">R$ 189,90</span>
      </div>
    </>
  );
}

export function SiteFiscalMock() {
  return (
    <div className="rounded-md border border-[#d9d4cc] bg-white p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] text-[#5c5852]">Documento fiscal</p>
          <p className="mt-1 text-sm font-medium">NFC-e 000182</p>
        </div>
        <span className="rounded-md bg-[#e8f6ee] px-2 py-1 text-[11px] font-semibold text-[#1f6b43]">Autorizada</span>
      </div>
      <p className="mt-3 text-[10px] text-[#5c5852]">Chave de acesso</p>
      <div className="mt-1.5 flex h-9 items-stretch" aria-hidden>
        {[1, 1, 2, 1, 3, 1, 1, 2, 1, 1, 3, 2, 1, 1, 2, 1, 3, 1, 1, 2, 1, 1, 1, 3, 1, 2, 1, 1, 3, 1, 2, 1, 1, 2, 3, 1, 1, 2, 1, 1, 3, 1, 1, 2].map(
          (width, index) => (
            <span
              key={index}
              className={index % 2 === 0 ? 'bg-[#16181d]' : 'bg-transparent'}
              style={{ width: `${width * 3}px` }}
            />
          ),
        )}
      </div>
      <p className="mt-1.5 break-all font-os text-[10px] leading-4 tracking-[0.08em] text-[#5c5852]">
        3526 0812 3456 7800 0199 5500 1000 1821 2345 6789 0123
      </p>
      <div className="mt-4 flex justify-between border-t border-[#d9d4cc] pt-3 text-sm">
        <span>Venda PDV · João Pereira</span>
        <span className="font-os">R$ 189,90</span>
      </div>
    </div>
  );
}

export function SiteFinanceiroMock() {
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-md border border-[#d9d4cc] bg-white p-3">
          <p className="text-[11px] text-[#5c5852]">Entradas hoje</p>
          <p className="mt-1 font-os text-xl text-[#16181d]">R$ 4.280</p>
        </div>
        <div className="rounded-md border border-[#d9d4cc] bg-white p-3">
          <p className="text-[11px] text-[#5c5852]">A receber</p>
          <p className="mt-1 font-os text-xl text-[#16181d]">R$ 890</p>
        </div>
      </div>
      <ul className="mt-3 divide-y divide-[#d9d4cc] overflow-hidden rounded-md border border-[#d9d4cc] bg-white">
        <li className="flex items-center justify-between px-3 py-2.5 text-sm">
          <span>Troca de óleo · João</span>
          <span className="rounded bg-[#e8f6ee] px-1.5 py-0.5 text-[11px] font-semibold text-[#1f6b43]">PIX</span>
        </li>
        <li className="flex items-center justify-between px-3 py-2.5 text-sm">
          <span>OS-1038 · Marina</span>
          <span className="rounded border border-[#f3d2b3] bg-[#fff4ea] px-1.5 py-0.5 text-[11px] font-medium text-[#b15a10]">
            vence em 2d
          </span>
        </li>
      </ul>
    </>
  );
}
